<template>
    <div class="dish-details-select">
        <div class="dish-details-select__head">
            <span class="dish-details-select__eyebrow">{{ data.name }}</span>
            <span v-if="isRequired"
                  class="dish-details-select__required">{{ $t('pages.menu.required') }}</span>
        </div>

        <div class="dish-details-select__option"
             v-for="option in comOptions"
             :key="option.id"
             :class="{ 'is-selected': isOptionSelected(option) }">

            <component :is="getComponentType"
                       class="dish-details-select__control"
                       v-model="comModel"
                       :inputValue="option"
                       :label="option.product.name"
                       :name="radioName + data.id" />

            <span class="dish-details-select__price">{{ `+${option.price} zł` }}</span>

        </div>
    </div>
</template>

<script>
import CheckboxButton from '@/components/CheckboxButton.vue';
import RadioButton from '@/components/RadioButton.vue';
import RadioButtonWithQuantity from './RadioButtonWithQuantity.vue';
import CheckboxWithQuantity from './CheckboxWithQuantity.vue';
export default {
    components: {
        CheckboxButton,
        RadioButton,
        RadioButtonWithQuantity,
        CheckboxWithQuantity
    },
    props: {
        data: {
            type: Object,
            reqired: true,
        },
        radioName: {
            type: [String, Number],
            default: ""
        },
        value: { type: null },
    },
    data() {
        return {
            selectedItems: [],
        };
    },
    // watch: {
    //     comModel(nv) {
    //         console.log("comModel", nv);
    //     }
    // },
    computed: {
        getComponentType() {
            if (this.isCheckbox) {
                return this.hasQuantity ? 'CheckboxWithQuantity' : 'CheckboxButton';
            } else {
                return this.hasQuantity ? 'RadioButtonWithQuantity' : 'RadioButton';
            }
        },
        comOptions() {
            return this.data.options.map(o => ({
                id: o.id,
                price: o.price,
                product: o,
                quantity: o.quantity || 1,
            }));
        },
        comModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
        isCheckbox() {
            return this.data.is_multiselect;
        },
        hasQuantity() {
            return this.data.is_multiple;
        },
        isRequired() {
            return this.data.is_required;
        }
    },
    methods: {
        // Presentational-only helper: derives the selected state of a row from the
        // existing v-model value so the toggle-card can highlight. Does NOT mutate
        // selection state — selection/quantity logic still lives in the child controls.
        isOptionSelected(option) {
            const val = this.value;
            if (Array.isArray(val)) {
                return val.some(item => item && item.id === option.id);
            }
            return !!val && val.id === option.id;
        },
    },
};
</script>


<style lang="scss" scoped>
.dish-details-select {
    display: grid;
    gap: 10px;

    &__head {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 2px 2px;
    }

    &__eyebrow {
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        font-weight: var(--weight-bold);
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: var(--text-muted);
    }

    &__required {
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        font-weight: var(--weight-bold);
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--color-primary);
        background-color: var(--color-primary-soft);
        border-radius: var(--radius-pill);
        padding: 3px 10px;

        // Brand text is hard to read on the soft chip in dark mode — use white.
        :root[data-theme="dark"] & {
            color: #fff;
        }
    }

    /* each selectable extra/option row = a toggle card */
    &__option {
        display: flex;
        align-items: center;
        gap: 12px;

        padding: 13px 14px;
        border-radius: var(--radius-2xl);
        border: 1.5px solid var(--border-subtle);
        background-color: var(--surface-card);

        transition: border-color var(--dur-fast) var(--ease-standard),
                    background-color var(--dur-fast) var(--ease-standard);

        &.is-selected {
            border-color: var(--color-primary);
            background-color: var(--color-primary-soft);
        }
    }

    &__control {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__price {
        flex-shrink: 0;
        margin-left: auto;

        font-family: var(--font-sans);
        font-weight: var(--weight-bold);
        font-size: var(--text-base);

        color: var(--text-price);
    }
}

/* Re-skin the shared Checkbox/Radio controls to the new vocabulary, scoped to
   the dish-details option card only (the shared components stay untouched). */
.dish-details-select__control::v-deep {
    .checkbox-wrapper,
    .radio-wrapper {
        width: 100%;
    }

    .checkbox-label,
    .radio-label {
        padding-left: 34px;
        font-family: var(--font-sans);
        font-size: var(--text-md);
        font-weight: var(--weight-semibold);
        color: var(--text-primary);
        line-height: 1.3;
    }

    .icon-off,
    .icon-on {
        width: 22px;
        height: 22px;
    }

    /* Selection indicators — venue-coloured, no hardcoded violet (the shared icons
       ship #7019FD). Unselected = neutral outline; selected = a solid brand chip
       with a white mark so it stays visible on the brand-soft selected card. */
    .icon-off rect {
        stroke: var(--border-strong);
    }

    .icon-on rect {
        fill: var(--color-primary);
        stroke: var(--color-primary);
    }

    .icon-on rect + rect {   /* radio inner dot */
        fill: #fff;
        stroke: #fff;
    }

    .icon-on path {          /* checkbox check mark */
        fill: #fff;
    }

    /* The quantity stepper appears on the brand-soft selected card, where the
       default soft-fill buttons blend in — make them solid brand with white glyphs. */
    .v-counter > svg {
        rect { fill: var(--color-primary); }
        path { fill: #fff; }
    }

    /* CheckboxWithQuantity / RadioButtonWithQuantity stack the stepper below */
    .checkbox-quantity-wrap,
    .radiobutton-quantity-wrap {
        width: 100%;
    }
}
</style>
